import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Spinner,
} from "@empite/ui-components";
import { isApiError } from "@empite/api-client";
import type {
  Amount,
  LedgerTransaction,
  Operator,
  Reservation,
  TopUp,
  Wallet,
} from "../admin-client";
import { useAlertThreshold, useSetAlertThreshold, useWallet, useWalletLedger } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can, errorMessage } from "./guards";
import { Money, OptionalMoney, ShortID, SignedMoney, StatusBadge, when } from "./common";
import { ReasonButton } from "./reason-prompt";
import { MeasureValue } from "./alerts";
import { currencyExponent, formatUnits, isUnitsError, parseUnits } from "./money-units";

/**
 * One wallet, and everything that has moved through it.
 *
 * The page is four cards in the order an operator actually asks the
 * questions: what is in it, what is being held out of it right now, how it
 * was funded, and — last and longest — every posting that produced the
 * number at the top.
 *
 * There is no control on this page that changes anything. The ledger is
 * append-only in the database, and a correction is a compensating
 * transaction the platform posts, not an edit made from a console. A "fix
 * balance" button here would be a button that can destroy the evidence of
 * having been pressed.
 */
const LEDGER_PAGE = 100;

export function WalletDetail({ walletId }: { walletId: string }) {
  return (
    <RequireOperator>
      {(session) => <WalletPage walletId={walletId} operator={session.operator} />}
    </RequireOperator>
  );
}

function WalletPage({ walletId, operator }: { walletId: string; operator: Operator }) {
  const detail = useWallet(walletId);

  if (detail.isPending) {
    return <Spinner label="Loading the wallet" />;
  }
  if (detail.error || !detail.data) {
    return <ErrorAlert error={detail.error} title="Could not load the wallet" />;
  }

  const {
    wallet,
    reservations,
    top_ups: topUps,
    subscription_resets_at: resetsAt,
  } = detail.data;
  const held = reservations.filter((row) => row.status === "held");
  const stale = reservations.filter((row) => row.stale);
  // Absent means this wallet has never had a subscription. Zero credit with a
  // date is a DIFFERENT state — a subscriber who has spent the whole
  // allowance — and the two must not render the same, because only one of
  // them means "expect $25 back on that date".
  const hasSubscription = Boolean(resetsAt) || wallet.subscription_credit.micros !== 0;

  return (
    <>
      <PageHeader
        title={wallet.owner_label || wallet.owner_id}
        description={`${wallet.application_name} (${wallet.application_slug}) — ${wallet.currency}`}
      />

      {/* Drift interrupts. The cache is written in the same transaction as the
          entries it summarises, so a disagreement is a bug in the platform,
          not a number that will settle down on its own. */}
      {wallet.cache_stale ? (
        <Alert tone="danger" title="This wallet's cached balance disagrees with its ledger">
          The figures below are summed from {wallet.entry_seq.toLocaleString()} ledger entries and
          are the authority. billing.wallets claims {wallet.cached_available.amount}{" "}
          {wallet.cached_available.currency} available at entry{" "}
          {wallet.cache_entry_seq.toLocaleString()}. Raise a reconciliation.
        </Alert>
      ) : null}

      {stale.length > 0 ? (
        <Alert tone="warning" title="Funds are held past their expiry">
          {stale.length} reservation{stale.length === 1 ? " is" : "s are"} still held after the
          moment {stale.length === 1 ? "it" : "they"} should have been released. That is money the
          customer cannot spend and nobody is charging for — the stale-reservation sweep has not
          reached {stale.length === 1 ? "it" : "them"}.
        </Alert>
      ) : null}

      <Card
        title="Balance"
        actions={
          <>
            <StatusBadge status={wallet.status} />
            {wallet.cache_stale ? <Badge tone="danger">cache drifted</Badge> : null}
          </>
        }
      >
        {/* The total FIRST and its two halves beside it, because "what can
            this customer spend" is the question and the split is the caveat.
            Both halves are shown even when one is zero: a missing figure and
            a zero figure look the same and only one of them is a fact. */}
        <div className="esm-figures">
          <Figure label="Available to spend" value={wallet.available} />
          <Figure
            label={hasSubscription ? "…of which subscription credit" : "Subscription credit"}
            value={wallet.subscription_credit}
          />
          <Figure label="…of which top-up credit" value={wallet.topup_credit} />
          <Figure label={`Reserved (${held.length} held)`} value={wallet.reserved} />
        </div>
        {/* The sentence an operator repeats down the phone. It is here rather
            than only in the definition list below because the number above it
            is the one that will be wrong tomorrow, and a date buried in a
            fact list is a date nobody quotes. */}
        <p className="esm-muted-text">
          {hasSubscription ? (
            resetsAt ? (
              <>
                The subscription portion is this cycle's allowance and is spent BEFORE top-up
                credit. Whatever is left of it is burned at <strong>{when(resetsAt)}</strong>, when
                the paid period ends. Top-up credit was bought outright and never expires.
              </>
            ) : (
              <>
                The subscription portion is this cycle's allowance and is spent before top-up
                credit, but no cycle on this wallet is currently granted — nothing is due to
                expire. Top-up credit was bought outright and never expires.
              </>
            )
          ) : (
            <>This wallet has never held a subscription: all of its credit was bought outright and none of it expires.</>
          )}
        </p>
        {/* Stated in words, on the page, because it is the single fact most
            often got wrong in a support conversation. */}
        <p className="esm-muted-text">
          Credit is held per application and is not transferable. This balance can only be spent in{" "}
          <code>{wallet.application_slug}</code>; the same customer's other applications have their
          own wallets and their own balances.
        </p>
        <dl className="esm-detail-list">
          <Fact label="Wallet">
            <code>{wallet.id}</code>
          </Fact>
          <Fact label="Billing account">
            <code>{wallet.billing_account_id}</code>
          </Fact>
          <Fact label="Owner">
            {wallet.owner_label || "—"} <span className="esm-muted-text">({wallet.owner_type})</span>
          </Fact>
          <Fact label="Application">
            <Link to="/applications/$applicationId" params={{ applicationId: wallet.application_id }}>
              {wallet.application_name}
            </Link>
          </Fact>
          {/* Absent is stated in words. An em dash here would read as "this
              console did not load the date", which is the one reading that
              would send an operator looking for a bug. */}
          <Fact label="Subscription credit resets">
            {resetsAt ? when(resetsAt) : "Nothing granted — nothing to expire"}
          </Fact>
          <Fact label="Derived at entry">{wallet.entry_seq.toLocaleString()}</Fact>
          <Fact label="Cache written">{when(wallet.cached_at)}</Fact>
          <Fact label="Opened">{when(wallet.created_at)}</Fact>
          <Fact label="Last movement">{when(wallet.updated_at)}</Fact>
        </dl>
      </Card>

      <Card title="Reservations">
        <DataTable<Reservation>
          rows={reservations}
          rowKey={(row) => row.id}
          empty="Nothing has ever been reserved against this wallet."
          columns={[
            {
              key: "status",
              header: "Status",
              render: (row) => (
                <>
                  <StatusBadge status={row.status} />
                  {row.stale ? <Badge tone="danger">past expiry</Badge> : null}
                </>
              ),
            },
            {
              key: "reserved",
              header: "Held",
              align: "end",
              render: (row) => <Money value={row.reserved} />,
            },
            {
              key: "settled",
              header: "Charged",
              align: "end",
              render: (row) => <OptionalMoney value={row.settled} />,
            },
            {
              key: "released",
              header: "Returned",
              align: "end",
              render: (row) => <OptionalMoney value={row.released} />,
            },
            {
              key: "request",
              header: "Inference request",
              render: (row) => <ShortID value={row.inference_request_id} />,
            },
            { key: "created", header: "Taken", render: (row) => when(row.created_at) },
            { key: "expires", header: "Expires", render: (row) => when(row.expires_at) },
          ]}
        />
      </Card>

      <Card title="Top-ups">
        <DataTable<TopUp>
          rows={topUps}
          rowKey={(row) => row.id}
          empty="This wallet has never been topped up."
          columns={[
            { key: "status", header: "Status", render: (row) => <TopUpStatusBadge topUp={row} /> },
            {
              key: "amount",
              header: "Amount",
              align: "end",
              render: (row) => <Money value={row.amount} />,
            },
            {
              key: "provider",
              header: "Provider",
              // The session and intent ids, never the checkout URL: that URL
              // is the customer's payment page and the Admin API does not
              // send one.
              render: (row) => (
                <>
                  <code>{row.provider}</code>
                  <div className="esm-muted-text">
                    <ShortID value={row.provider_payment_intent_id} />
                  </div>
                </>
              ),
            },
            {
              key: "posted",
              header: "Ledger",
              // A succeeded top-up ALWAYS has a transaction — the database
              // constraint says so — which is why an empty cell here on a
              // succeeded row would mean the constraint had been dropped.
              render: (row) => <ShortID value={row.ledger_transaction_id} />,
            },
            {
              key: "failure",
              header: "Failure",
              render: (row) =>
                row.failure_reason ? row.failure_reason : <span className="esm-muted-text">—</span>,
            },
            { key: "created", header: "Started", render: (row) => when(row.created_at) },
            { key: "completed", header: "Completed", render: (row) => when(row.completed_at) },
          ]}
        />
      </Card>

      <RunoutThresholdCard wallet={wallet} operator={operator} />

      <LedgerCard walletId={walletId} />
    </>
  );
}

/**
 * The ledger is its own query, behind its own permission.
 *
 * `ledger:read` is a separate grant from `wallets:read`, so an operator who
 * may see whether a wallet is funded but not every charge the customer has
 * ever made gets a 403 in THIS card and a working page everywhere else. If it
 * were a field on the wallet response, the whole page would fail for them.
 */
function LedgerCard({ walletId }: { walletId: string }) {
  const ledger = useWalletLedger(walletId, LEDGER_PAGE);

  if (ledger.error) {
    const forbidden = isApiError(ledger.error) && ledger.error.status === 403;
    return (
      <Card title="Ledger">
        <Alert tone={forbidden ? "info" : "danger"} title={forbidden ? "Not your permission" : "Could not load the ledger"}>
          {forbidden
            ? "Reading a customer's postings needs ledger:read, which your roles do not grant. The balance above needs only wallets:read."
            : errorMessage(ledger.error)}
        </Alert>
      </Card>
    );
  }

  return (
    <Card title={`Ledger — the last ${LEDGER_PAGE} postings`}>
      {ledger.isPending ? (
        <Spinner label="Loading the ledger" />
      ) : (
        <DataTable<LedgerTransaction>
          rows={ledger.data?.transactions ?? []}
          rowKey={(row) => row.id}
          empty="Nothing has ever been posted against this wallet."
          columns={[
            { key: "occurred", header: "When", render: (row) => when(row.occurred_at) },
            {
              key: "kind",
              header: "Kind",
              render: (row) => (
                <>
                  <code>{row.kind}</code>
                  {row.description ? (
                    <div className="esm-muted-text">{row.description}</div>
                  ) : null}
                </>
              ),
            },
            {
              key: "delta",
              header: "Effect on this wallet",
              align: "end",
              // Zero here is not "nothing happened": a reservation moves money
              // between the wallet's OWN available and reserved accounts, and
              // nets to zero on the wallet as a whole. The legs below say so.
              render: (row) => <SignedMoney value={row.wallet_delta} />,
            },
            {
              key: "entries",
              header: "Double entry",
              // Every leg, including the platform ones. A charge debits the
              // wallet and credits revenue and provider cost; an operator who
              // can only see their side of it cannot tell a charge from a
              // transfer, and cannot see that the legs sum to zero.
              render: (row) => (
                <ul className="esm-entries">
                  {row.entries.map((entry) => (
                    <li key={entry.id}>
                      <code>{entry.account_kind}</code>
                      <SignedMoney value={entry.amount} />
                    </li>
                  ))}
                </ul>
              ),
            },
            {
              key: "reference",
              header: "Reference",
              render: (row) =>
                row.reference_type ? (
                  <>
                    <code>{row.reference_type}</code>
                    <div className="esm-muted-text">
                      <ShortID value={row.reference_id} />
                    </div>
                  </>
                ) : (
                  <span className="esm-muted-text">—</span>
                ),
            },
          ]}
        />
      )}
    </Card>
  );
}

/**
 * The line THIS account's wallets are measured against, and the one control on
 * this page that writes anything.
 *
 * It is here rather than on `/alerts` because this is where an operator
 * actually has a billing account id in their hand. The threshold is filed per
 * BILLING ACCOUNT and not per wallet — one company, one line, however many
 * applications they hold credit in — which is why the card says whose it is:
 * saving here moves the warning for every wallet on the account, not just the
 * one above it.
 *
 * A null threshold is the PLATFORM FLOOR and is the normal state of an account
 * nobody has tuned. It is stated in words for that reason; an empty figure
 * here would read as a card that failed to load, and an operator would go
 * looking for a bug instead of a decision.
 *
 * The editor is drawn only for `wallets:adjust`. Hiding it is a courtesy — the
 * Admin API's Guard re-checks the grant and audits the refusal, and the reason
 * middleware refuses the PUT outright without a code — so an operator without
 * it still sees the figure, which is a `wallets:read` fact like everything
 * else on the page.
 */
function RunoutThresholdCard({ wallet, operator }: { wallet: Wallet; operator: Operator }) {
  const current = useAlertThreshold(wallet.billing_account_id);
  const save = useSetAlertThreshold();
  const mayAdjust = can(operator, "wallets:adjust");
  const threshold = current.data?.threshold ?? null;

  // The wallet names its currency, so there is no guessing to do about WHICH
  // currency — but the exponent is what decides whether "5" is 500,000,000
  // micros or 5,000,000, and an exponent this console does not hold is one it
  // must not assume. internal/money refuses an unknown code rather than
  // assuming two decimal places; a console that assumed instead would be the
  // one place in the platform that guessed, and it would be wrong by 100x on a
  // JPY account with nothing on screen to say so.
  const exponent = currencyExponent(wallet.currency);

  return (
    <Card
      title="Runout alert threshold"
      actions={
        threshold ? (
          <Badge tone={threshold.enabled ? "success" : "neutral"}>
            {threshold.enabled ? "override in force" : "override held, not applied"}
          </Badge>
        ) : (
          <Badge tone="neutral">platform floor</Badge>
        )
      }
    >
      <ErrorAlert error={current.error} title="Could not load the threshold" />
      {current.isPending ? (
        <Spinner label="Loading the threshold" />
      ) : (
        <>
          <p className="esm-muted-text">
            When the available balance on a wallet of billing account{" "}
            <code>{wallet.billing_account_id}</code> falls below this line, the runout sweep
            raises one alert and stops raising it until the balance recovers. The line is the
            ACCOUNT's, so it governs every application this customer holds credit in — not only{" "}
            <code>{wallet.application_slug}</code>.
          </p>
          <dl className="esm-detail-list">
            <dt>Current threshold</dt>
            <dd>
              {threshold ? (
                <MeasureValue measure={threshold.measure} />
              ) : (
                "On the platform floor — this account has no line of its own."
              )}
            </dd>
            <dt>Applied</dt>
            <dd>
              {/* Disabled is not "no threshold". The row keeps its number and
                  stops being applied, so an account can be put back on its
                  tuned line without anybody remembering what it was — and a
                  card that drew the two states the same would hide a figure
                  somebody chose. */}
              {threshold
                ? threshold.enabled
                  ? "Yes — this account is measured against the figure above."
                  : "No — the figure above is kept but the account is measured against the platform floor."
                : "The platform floor applies."}
            </dd>
            {threshold ? (
              <>
                <dt>Last changed</dt>
                <dd>
                  {when(threshold.updated_at)}{" "}
                  <span className="esm-muted-text">
                    by <ShortID value={threshold.updated_by} />
                  </span>
                </dd>
              </>
            ) : null}
          </dl>

          {!mayAdjust ? (
            <p className="esm-muted-text">
              Changing it needs <code>wallets:adjust</code>, which your roles do not grant —
              deciding at what balance a customer is warned is the same class of act as adjusting
              the balance itself.
            </p>
          ) : exponent === undefined ? (
            // Refused rather than guessed. See the comment on `exponent`: there
            // is no honest conversion to offer for a currency whose decimal
            // places this console does not hold.
            <Alert tone="warning" title="This console cannot convert that currency">
              The wallet is denominated in <code>{wallet.currency}</code>, whose minor unit this
              portal does not know. Setting a threshold in it would mean guessing the scale, so the
              editor is not offered.
            </Alert>
          ) : (
            <RunoutThresholdForm
              wallet={wallet}
              exponent={exponent}
              // Remounted when the saved figure changes, which is what carries
              // a save back into the boxes: the fields are the operator's
              // draft and the query is the truth, and re-keying is how the
              // truth wins without an effect that could stamp on typing
              // mid-refetch.
              key={`${threshold?.id ?? "floor"}:${threshold?.updated_at ?? ""}`}
              currentMicros={
                // Only a MONEY threshold can prefill a money box. A measure in
                // another unit is a figure this form would silently
                // re-denominate.
                threshold && threshold.measure.unit === "micros" ? threshold.measure.value : null
              }
              currentEnabled={threshold?.enabled ?? true}
              save={save}
            />
          )}
        </>
      )}
    </Card>
  );
}

/**
 * The editor itself: an amount in the wallet's own currency, and whether the
 * line is applied.
 *
 * The box takes CURRENCY UNITS and the wire takes integer micros as a string.
 * The conversion is `parseUnits`, which does it in BigInt at the currency's
 * exponent — never a float, because `5.55 * 1e8` is 555000000.0000001 in IEEE
 * 754 and a threshold one micro off the one an operator typed is a
 * discrepancy nobody can explain six months later. The exact integer is echoed
 * under the box so the operator can check it before it is sent.
 */
function RunoutThresholdForm({
  wallet,
  exponent,
  currentMicros,
  currentEnabled,
  save,
}: {
  wallet: Wallet;
  exponent: number;
  currentMicros: number | null;
  currentEnabled: boolean;
  save: ReturnType<typeof useSetAlertThreshold>;
}) {
  // Grouping off, on money-units' own advice: a value the operator cannot
  // re-submit unchanged is a box that fights back.
  const [amount, setAmount] = useState(
    currentMicros === null ? "" : formatUnits(currentMicros, exponent, false),
  );
  const [enabled, setEnabled] = useState(currentEnabled);

  const trimmed = amount.trim();
  const parsed = trimmed === "" ? undefined : parseUnits(trimmed, exponent);
  // parseUnits' own refusal for a negative figure is worded for a spend limit,
  // which this is not — a threshold that is "no limit when empty" would be a
  // sentence that means nothing here. The rule is the same and the server
  // applies it either way (0064's `>= 0` CHECK); only the sentence differs.
  const error =
    trimmed.startsWith("-")
      ? "A threshold cannot be negative. Zero means the account is only warned once its wallet is empty."
      : parsed && isUnitsError(parsed)
        ? parsed.error
        : undefined;
  const micros = parsed && !isUnitsError(parsed) ? parsed.micros : undefined;
  const unchanged = micros !== undefined
    && currentMicros !== null
    && micros === BigInt(currentMicros)
    && enabled === currentEnabled;

  return (
    <>
      <ErrorAlert error={save.error} title="That threshold did not go through" />
      <div className="esm-filters">
        <Field
          label={`Warn below (${wallet.currency})`}
          htmlFor="runout-threshold-amount"
          hint={
            micros === undefined
              ? "In whole currency units — 5.00, not 500000000. The exact figure the request carries is shown here once it parses."
              : `= ${micros.toLocaleString()} micros`
          }
          error={error}
        >
          <Input
            id="runout-threshold-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
        {/* A checkbox and not a Field: Field wires one label to one control by
            id and puts the label above it, which reads as a heading over a
            tick box rather than as a sentence about it. */}
        <div className="esm-field">
          <input
            id="runout-threshold-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <label htmlFor="runout-threshold-enabled">
            Apply this threshold
            <span className="esm-muted-text">
              {" "}
              Unticked keeps the figure and puts the account back on the platform floor.
            </span>
          </label>
        </div>
      </div>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={save.isPending}
          disabled={micros === undefined || error !== undefined || unchanged}
          mutation={{
            title: "Set the runout alert threshold",
            tone: "primary",
            description: (
              <p>
                Every wallet on billing account <code>{wallet.billing_account_id}</code> will be
                measured against {trimmed} {wallet.currency}
                {enabled ? "" : ", once the threshold is applied again"}. It changes no balance and
                closes no alert that has already fired — it decides when the NEXT one is raised.
              </p>
            ),
            confirmLabel: "Set threshold",
            run: async (reason) => {
              if (micros === undefined) {
                return;
              }
              await save.mutateAsync({
                accountID: wallet.billing_account_id,
                body: {
                  kind: "wallet_runout",
                  enabled,
                  // A STRING, and the whole reason money-units works in
                  // BigInt: a JSON number is a float64 in this browser and is
                  // exact only to about ninety dollars' worth of micros.
                  amount: { currency: wallet.currency, micros: micros.toString() },
                },
                reason,
              });
            },
          }}
        >
          Set threshold
        </ReasonButton>
      </div>
    </>
  );
}

function Figure({ label, value }: { label: string; value: Amount }) {
  return (
    <div>
      <div className="esm-figure__value">
        <Money value={value} />
      </div>
      <div className="esm-figure__label">{label}</div>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/**
 * A top-up's status, with the one state that is not self-explanatory called
 * out: money in flight at the provider and nothing posted to the ledger.
 * `succeeded` cannot be in that state — a database constraint requires a
 * ledger transaction on it — so this badge can only ever appear mid-flight.
 */
function TopUpStatusBadge({ topUp }: { topUp: TopUp }) {
  return (
    <>
      <StatusBadge status={topUp.status} />
      {topUp.paid_not_credited ? <Badge tone="warning">not credited yet</Badge> : null}
    </>
  );
}
